import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { GetAllCrashlyticsErrors, CreateCrashlyticsError, UpdateCrashlyticsError, DeleteCrashlyticsError, ExportCrashlyticsErrors } from '../../helpers/api_method';
import { ErrorData } from '../../services/crashlyticsService';

interface CrashlyticsState {
  errors: ErrorData[];
  loading: boolean;
  error: string | null;
  filters: {
    type: string;
    severity: string;
    resolved: boolean | null;
    dateRange: {
      start: Date | null;
      end: Date | null;
    };
  };
  stats: {
    total: number;
    resolved: number;
    unresolved: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
  };
  pagination: {
    has_next: boolean;
    has_prev: boolean;
    page: number;
    pages: number;
    per_page: number;
    total: number;
  };
}

const initialState: CrashlyticsState = {
  errors: [],
  loading: false,
  error: null,
  filters: {
    type: '',
    severity: '',
    resolved: null,
    dateRange: {
      start: null,
      end: null,
    },
  },
  stats: {
    total: 0,
    resolved: 0,
    unresolved: 0,
    byType: {},
    bySeverity: {},
  },
  pagination: {
    has_next: false,
    has_prev: false,
    page: 1,
    pages: 1,
    per_page: 50,
    total: 0,
  },
};

// Async thunk to load errors from backend
export const loadErrorsFromBackend = createAsyncThunk(
  'crashlytics/loadErrorsFromBackend',
  async (filters: any = undefined, { rejectWithValue }) => {
    try {
      const response = await GetAllCrashlyticsErrors(filters);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || 'Failed to fetch crashlytics errors');
    }
  }
);

// Async thunk to create new error
export const createError = createAsyncThunk(
  'crashlytics/createError',
  async (errorData: any, { rejectWithValue }) => {
    try {
      const response = await CreateCrashlyticsError(errorData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || 'Failed to create crashlytics error');
    }
  }
);

// Async thunk to update error
export const updateError = createAsyncThunk(
  'crashlytics/updateError',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await UpdateCrashlyticsError(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || 'Failed to update crashlytics error');
    }
  }
);

// Async thunk to delete error
export const deleteError = createAsyncThunk(
  'crashlytics/deleteError',
  async (id: string, { rejectWithValue }) => {
    try {
      await DeleteCrashlyticsError(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || 'Failed to delete crashlytics error');
    }
  }
);

// Async thunk to export errors
export const exportErrors = createAsyncThunk(
  'crashlytics/exportErrors',
  async (filters: any = undefined, { rejectWithValue }) => {
    try {
      const response = await ExportCrashlyticsErrors(filters);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crashlytics-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message || 'Failed to export crashlytics errors');
    }
  }
);

const crashlyticsSlice = createSlice({
  name: 'crashlytics',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{
      key: keyof typeof state.filters;
      value: any;
    }>) => {
      (state.filters as any)[action.payload.key] = action.payload.value;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load errors from backend
      .addCase(loadErrorsFromBackend.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadErrorsFromBackend.fulfilled, (state: any, action: any) => {
        // Handle the actual API response structure
        const responseData = action.payload;
        let errors = [];
        let pagination = initialState.pagination;
        
        if (responseData) {
          // Handle both response structures
          if (responseData.data && responseData.data.errors) {
            // Structure: { data: { errors: [...], pagination: {...} } }
            errors = responseData.data.errors || [];
            pagination = responseData.data.pagination || initialState.pagination;
          } else if (responseData.errors) {
            // Structure: { errors: [...], pagination: {...} }
            errors = responseData.errors || [];
            pagination = responseData.pagination || initialState.pagination;
          }
        }
        
        // Ensure errors is an array
        if (!Array.isArray(errors)) {
          errors = [];
        }
        
        // Transform the data to match frontend expectations
        const transformedErrors = errors.map((error: any, index: number) => {
          try {
            return {
              id: error?.id || `error_${Date.now()}_${index}`,
              timestamp: error?.created_at ? new Date(error.created_at) : new Date(),
              type: error?.error_type || 'UNKNOWN',
              severity: (error?.severity as 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL') || 'MEDIUM',
              message: error?.message || 'Unknown error',
              stack: error?.stack_trace || '',
              url: error?.url || '',
              userAgent: error?.user_agent || '',
              userId: error?.user_id || '',
              userEmail: error?.user_email || '',
              deviceInfo: {
                platform: error?.device_platform || 'Unknown',
                browser: error?.device_browser || 'Unknown',
                version: error?.device_version || 'Unknown',
                screen: error?.device_screen || 'Unknown',
              },
              sessionInfo: {
                sessionId: error?.session_id || 'Unknown',
                pageLoadTime: error?.page_load_time || 'Unknown',
                memoryUsage: error?.memory_usage || 'Unknown',
              },
              apiDetails: error?.api_endpoint ? {
                endpoint: error?.api_endpoint || '',
                method: error?.api_method || '',
                statusCode: error?.api_status_code || '',
                requestData: error?.api_request_data || '',
                responseData: error?.api_response_data || '',
              } : undefined,
              resolved: Boolean(error?.resolved) || false,
              resolvedBy: error?.resolved_by || '',
              resolvedAt: error?.resolved_at ? new Date(error.resolved_at) : undefined,
              notes: error?.resolution_notes || '',
            } as ErrorData;
          } catch (transformError) {
            return {
              id: `error_${Date.now()}_${index}`,
              timestamp: new Date(),
              type: 'UNKNOWN',
              severity: 'MEDIUM',
              message: 'Error transforming data',
              stack: '',
              url: '',
              userAgent: '',
              userId: '',
              userEmail: '',
              deviceInfo: { platform: '', browser: '', version: '', screen: '' },
              sessionInfo: { sessionId: '', pageLoadTime: '', memoryUsage: '' },
              resolved: false,
              resolvedBy: '',
              notes: '',
            } as ErrorData;
          }
        });
        
        (state as any).errors = transformedErrors;
        (state as any).pagination = pagination;
        state.stats = calculateStats(state.errors);
        state.loading = false;
        state.error = null;
      })
      .addCase(loadErrorsFromBackend.rejected, (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load errors';
        state.errors = [];
        state.stats = calculateStats(state.errors);
      })
      
      // Create error
      .addCase(createError.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createError.fulfilled, (state: any, action: any) => {
        const newError = action.payload;
        state.errors.unshift(newError);
        state.stats = calculateStats(state.errors);
        state.loading = false;
        state.error = null;
      })
      .addCase(createError.rejected, (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create error';
      })
      
      // Update error
      .addCase(updateError.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateError.fulfilled, (state: any, action: any) => {
        const updatedError = action.payload;
        const index = state.errors.findIndex((e: any) => e.id === updatedError.id);
        if (index !== -1) {
          state.errors[index] = updatedError;
          state.stats = calculateStats(state.errors);
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateError.rejected, (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update error';
      })
      
      // Delete error
      .addCase(deleteError.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteError.fulfilled, (state: any, action: any) => {
        const deletedId = action.payload;
        state.errors = state.errors.filter((error: any) => error.id !== deletedId);
        state.stats = calculateStats(state.errors);
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteError.rejected, (state: any, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete error';
      });
  },
});

// Helper function to calculate stats
function calculateStats(errors: ErrorData[]) {
  // Ensure errors is always an array
  if (!Array.isArray(errors)) {
    return {
      total: 0,
      resolved: 0,
      unresolved: 0,
      byType: {},
      bySeverity: {},
    };
  }

  return {
    total: errors.length,
    resolved: errors.filter(e => e.resolved).length,
    unresolved: errors.filter(e => !e.resolved).length,
    byType: errors.reduce((acc, error) => {
      acc[error.type] = (acc[error.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    bySeverity: errors.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };
}

// Selectors
export const selectFilteredErrors = createSelector(
  [(state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.errors || [],
   (state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.filters || initialState.filters],
  (errors, filters) => {
    // Ensure errors is always an array
    if (!Array.isArray(errors)) {
      return [];
    }
    
    const filteredErrors = errors.filter(error => {
      if (filters.type && error.type !== filters.type) return false;
      if (filters.severity && error.severity !== filters.severity) return false;
      if (filters.resolved !== null && error.resolved !== filters.resolved) return false;
      if (filters.dateRange.start && error.timestamp < filters.dateRange.start) return false;
      if (filters.dateRange.end && error.timestamp > filters.dateRange.end) return false;
      return true;
    });
    
    return filteredErrors;
  }
);

export const selectErrorStats = (state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.stats || {
  total: 0,
  resolved: 0,
  unresolved: 0,
  byType: {},
  bySeverity: {}
};

export const selectLoading = (state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.loading || false;

export const selectError = (state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.error || null;

export const selectPagination = (state: { Crashlytics?: CrashlyticsState }) => state.Crashlytics?.pagination || {
  has_next: false,
  has_prev: false,
  page: 1,
  pages: 1,
  per_page: 50,
  total: 0,
};

export const {
  setFilter,
  clearFilters,
  clearError,
} = crashlyticsSlice.actions;

export default crashlyticsSlice.reducer;
